import { v4 as uuidv4 } from 'uuid';
import {
    type ContentMappings,
    type SendTo,
    type Variable,
} from '../types/campaign';
import { CommunicationChannel } from '../types/communications';
import { CampaignFieldType, FieldType } from '../types/field';
import {
    ActionType,
    type Branch,
    BranchConcurrencyTypes,
    BranchConditionalTypes,
    CallApiConcatOperator,
    type ConversionTrigger,
    type DateTimeConfig,
    DifferenceOperator,
    type DurationTimeConfig,
    type ExitTrigger,
    HoldBoundaryType,
    type HoldConfig,
    JOURNEY_DYNAMIC_VARIABLE_PREFIX,
    type JourneyAction,
    type JourneyAndVersionConfig,
    type JourneyBlocksMetadata,
    JourneyConditionalOperator,
    type JourneyFilterRule,
    type JourneyFiltersConfig,
    JourneyGroup,
    type JourneyNode,
    type JourneyNodesConfig,
    JourneyParamsNames,
    TimeConfigType,
    WAIT_FOR_WH_CACHE_BLOCK_ID,
} from '../types/journeys';
import assertUnreachable from './assertUnreachable';
import { getActionSeedData } from './journeyActionSeeds';
import { hasMeaningfulNamingSignal } from './journeyNaming/eligibility';

/** Block catalog id for AI Classify */
export const AI_CLASSIFY_BLOCK_ID = '14';
/** Block catalog id for Split */
export const SPLIT_BLOCK_ID = '4';

export const journeyBlocks: JourneyBlocksMetadata[] = [
    {
        id: '1',
        title: 'Delay',
        description: undefined,
        group: JourneyGroup.DELAY,
        actions: [
            {
                actionType: ActionType.WAIT,
                config: {
                    type: TimeConfigType.DURATION,
                    allowEveryoneElse: false,
                },
            },
        ],
        allowedInExperiment: true, // TODO: currently only the Wait block is enabled inside experiments; enable remaining blocks (Send Message, Update Trait, etc.) in follow-up PRs.
    },
    {
        id: '2',
        title: 'Wait for action',
        description: undefined,
        group: JourneyGroup.DELAY,
        actions: [
            {
                actionType: ActionType.WAIT_UNTIL,
                config: {
                    allowEveryoneElse: true,
                },
            },
        ],
        allowedInExperiment: false,
    },
    {
        id: '3',
        title: 'Filter',
        description: undefined,
        group: JourneyGroup.FLOW_CONTROL,
        actions: [
            {
                actionType: ActionType.FILTER,
                config: {
                    allowEveryoneElse: true,
                },
            },
        ],
        allowedInExperiment: false,
    },
    {
        id: '4',
        title: 'Split',
        description: undefined,
        group: JourneyGroup.FLOW_CONTROL,
        actions: [
            {
                actionType: ActionType.SPLIT,
                config: {
                    allowEveryoneElse: true,
                },
            },
        ],
        allowedInExperiment: false,
    },
    {
        id: '5',
        title: 'Experiment',
        description: undefined,
        group: JourneyGroup.FLOW_CONTROL,
        actions: [
            {
                actionType: ActionType.EXPERIMENT,
                config: {
                    allowEveryoneElse: false,
                },
            },
        ],
        allowedInExperiment: false,
    },
    {
        id: '6',
        title: 'Send Email',
        description: undefined,
        group: JourneyGroup.ACTION,
        actions: [
            {
                actionType: ActionType.SEND_MESSAGE,
                config: {
                    channelType: CommunicationChannel.EMAIL,
                    allowEveryoneElse: false,
                },
            },
        ],
        allowedInExperiment: true,
    },
    {
        id: '7',
        title: 'Send SMS',
        description: undefined,
        group: JourneyGroup.ACTION,
        actions: [
            {
                actionType: ActionType.SEND_MESSAGE,
                config: {
                    channelType: CommunicationChannel.SMS,
                    allowEveryoneElse: false,
                },
            },
        ],
        allowedInExperiment: true,
    },
    {
        id: '8',
        title: 'Send WhatsApp',
        description: undefined,
        group: JourneyGroup.ACTION,
        actions: [
            {
                actionType: ActionType.SEND_MESSAGE,
                config: {
                    channelType: CommunicationChannel.WHATSAPP,
                    allowEveryoneElse: false,
                },
            },
        ],
        allowedInExperiment: true,
    },
    {
        id: '9',
        title: 'Send Push Notification',
        description: undefined,
        group: JourneyGroup.ACTION,
        actions: [
            {
                actionType: ActionType.SEND_MESSAGE,
                config: {
                    channelType: CommunicationChannel.PUSH,
                    allowEveryoneElse: false,
                },
            },
        ],
        allowedInExperiment: true,
    },
    {
        id: '10',
        title: 'Call API',
        description: undefined,
        group: JourneyGroup.ACTION,
        actions: [
            {
                actionType: ActionType.API,
                config: {
                    allowEveryoneElse: false,
                },
            },
        ],
        allowedInExperiment: true,
    },
    {
        id: '11',
        title: 'Wait till date',
        description: undefined,
        group: JourneyGroup.DELAY,
        actions: [
            {
                actionType: ActionType.WAIT,
                config: {
                    type: TimeConfigType.DATE_TIME,
                    allowEveryoneElse: true,
                },
            },
        ],
        allowedInExperiment: false,
    },
    {
        id: '12',
        title: 'Update attribute',
        description: undefined,
        group: JourneyGroup.ACTION,
        actions: [
            {
                actionType: ActionType.UPDATE_TRAIT,
            },
        ],
        allowedInExperiment: true,
    },
    {
        id: '13',
        title: 'Add to journey',
        description: undefined,
        group: JourneyGroup.ACTION,
        actions: [
            {
                actionType: ActionType.SEND_TO_JOURNEY,
            },
        ],
        allowedInExperiment: true,
    },
    {
        id: '14',
        title: 'AI Classify',
        description: 'Classify user input using AI',
        group: JourneyGroup.FLOW_CONTROL,
        actions: [
            {
                actionType: ActionType.AI_CLASSIFY,
            },
        ],
        allowedInExperiment: false,
    },
];

export const isAllBranchDestinationsAreValid = (
    journey: JourneyNodesConfig,
): { isValid: boolean; invalidBranchesNodes: JourneyNode[] } => {
    const allNodeIds = new Set(journey.nodes.map((node) => node.id));

    const invalidBranchesNodes = journey.nodes.flatMap((node) => {
        const branches = node.branchConfig?.children?.branches ?? [];
        const hasInvalidBranch = branches.some(
            (branch) =>
                !branch.destination || !allNodeIds.has(branch.destination),
        );
        return hasInvalidBranch ? [node] : [];
    });

    return {
        isValid: invalidBranchesNodes.length === 0,
        invalidBranchesNodes,
    };
};
export const getBranchLimits = (actionType: ActionType) => {
    switch (actionType) {
        case ActionType.SPLIT:
            // Split: 0 or 1 default branch and 0 to many branches
            return {
                minDefaultBranches: 0,
                maxDefaultBranches: 1,
                minNonDefaultBranches: 0,
                maxNonDefaultBranches: Number.MAX_SAFE_INTEGER,
            };
        case ActionType.FILTER:
            // Filter: 0-1 default, 0-1 non-default
            return {
                minDefaultBranches: 0,
                maxDefaultBranches: 1,
                minNonDefaultBranches: 0,
                maxNonDefaultBranches: 1,
            };
        case ActionType.WAIT:
            // Wait: 0-1 non-default, 0-1 default
            return {
                minDefaultBranches: 0,
                maxDefaultBranches: 1,
                minNonDefaultBranches: 0,
                maxNonDefaultBranches: 1,
            };
        case ActionType.WAIT_UNTIL:
            // Wait until: 0-1 default, 0-1 non-default
            return {
                minDefaultBranches: 0,
                maxDefaultBranches: 1,
                minNonDefaultBranches: 0,
                maxNonDefaultBranches: 1,
            };
        case ActionType.EXPERIMENT:
            // Experiment: 0-1 non-default
            return {
                minDefaultBranches: 0,
                maxDefaultBranches: 0,
                minNonDefaultBranches: 0,
                maxNonDefaultBranches: Number.MAX_SAFE_INTEGER,
            };
        case ActionType.SEND_MESSAGE:
            // Send message: 0-1 non-default
            return {
                minDefaultBranches: 0,
                maxDefaultBranches: 0,
                minNonDefaultBranches: 0,
                maxNonDefaultBranches: 1,
            };
        case ActionType.API:
            // API: 0-1 non-default
            return {
                minDefaultBranches: 0,
                maxDefaultBranches: 0,
                minNonDefaultBranches: 0,
                maxNonDefaultBranches: 1,
            };
        case ActionType.UPDATE_TRAIT:
            // Update trait: 0-1 non-default
            return {
                minDefaultBranches: 0,
                maxDefaultBranches: 0,
                minNonDefaultBranches: 0,
                maxNonDefaultBranches: 1,
            };
        case ActionType.SEND_TO_JOURNEY:
            // Send to journey: 0-1 non-default
            return {
                minDefaultBranches: 0,
                maxDefaultBranches: 0,
                minNonDefaultBranches: 0,
                maxNonDefaultBranches: 1,
            };
        case ActionType.SET:
            // Set: can have branches like other action types
            return {
                minDefaultBranches: 0,
                maxDefaultBranches: Number.MAX_SAFE_INTEGER,
                minNonDefaultBranches: 0,
                maxNonDefaultBranches: Number.MAX_SAFE_INTEGER,
            };
        case ActionType.AI_CLASSIFY:
            return {
                minDefaultBranches: 0,
                maxDefaultBranches: 1,
                minNonDefaultBranches: 0,
                maxNonDefaultBranches: Number.MAX_SAFE_INTEGER,
            };
        default:
            // No branches for unknown action types
            return {
                minDefaultBranches: 0,
                maxDefaultBranches: 0,
                minNonDefaultBranches: 0,
                maxNonDefaultBranches: 0,
            };
    }
};

export const isNumberOfBranchesPerNodeValid = (
    journey: JourneyNodesConfig,
): { isValid: boolean; invalidNodes: JourneyNode[] } => {
    const invalidNodes: JourneyNode[] = [];

    journey.nodes.forEach((node) => {
        const branches = node.branchConfig?.children?.branches ?? [];
        const defaultBranches = branches.filter((branch) => branch.isDefault);
        const nonDefaultBranches = branches.filter(
            (branch) => !branch.isDefault,
        );

        const actionType = node.actions[0]?.type;
        if (!actionType) return;

        const limits = getBranchLimits(actionType);

        const isValid =
            defaultBranches.length >= limits.minDefaultBranches &&
            defaultBranches.length <= limits.maxDefaultBranches &&
            nonDefaultBranches.length >= limits.minNonDefaultBranches &&
            nonDefaultBranches.length <= limits.maxNonDefaultBranches;

        if (!isValid) {
            invalidNodes.push(node);
        }
    });

    return {
        isValid: invalidNodes.length === 0,
        invalidNodes,
    };
};

// Distribution percentages are rounded to 2 decimal places independently
// per variant (both via UI auto-balance and API-authored configs), so an
// even split can legitimately land a little above or below 100 (e.g.
// splitting 100 across 3 variants gives 33.33 + 33.33 + 33.34, and across
// 6 gives 16.67 * 6 = 100.02). Allow for that rounding drift while still
// rejecting configs that are meaningfully wrong, e.g. a missing variant.
export const EXPERIMENT_DISTRIBUTION_PERCENTAGE_TOLERANCE = 1;

export const isExperimentVariantDistributionValid = (
    journey: JourneyNodesConfig,
): { isValid: boolean; invalidNodes: JourneyNode[] } => {
    const invalidNodes: JourneyNode[] = [];

    journey.nodes.forEach((node) => {
        const experimentActions = node.actions.filter(
            (action) => action.type === ActionType.EXPERIMENT,
        );

        const hasInvalidDistribution = experimentActions.some((action) => {
            if (action.type !== ActionType.EXPERIMENT) return false;
            const { variantConfig } = action.config.experimentConfig;
            const totalPercentage = variantConfig.reduce(
                (sum, variant) => sum + variant.distributionPercentage,
                0,
            );
            return (
                Math.abs(totalPercentage - 100) >
                EXPERIMENT_DISTRIBUTION_PERCENTAGE_TOLERANCE
            );
        });

        if (hasInvalidDistribution) {
            invalidNodes.push(node);
        }
    });

    return {
        isValid: invalidNodes.length === 0,
        invalidNodes,
    };
};

export const changeCampaignIdForSendMessageActions = (
    journeyNodesConfig: JourneyNodesConfig,
): JourneyNodesConfig => {
    const campaignIdMapping = new Map<string, string>();

    journeyNodesConfig.nodes.forEach((node) => {
        if (node.actions) {
            node.actions.forEach((action) => {
                if (
                    action.type === ActionType.SEND_MESSAGE &&
                    action.config.campaignId
                ) {
                    const oldCampaignId = action.config.campaignId;
                    if (!campaignIdMapping.has(oldCampaignId)) {
                        campaignIdMapping.set(oldCampaignId, uuidv4());
                    }
                }
            });
        }
    });
    const updatedNodes = journeyNodesConfig.nodes.map((node) => {
        if (node.actions) {
            const newActions = node.actions.map((action) => {
                if (action.type === ActionType.SEND_MESSAGE) {
                    const newCampaignId = action.config.campaignId
                        ? campaignIdMapping.get(action.config.campaignId) ||
                          uuidv4()
                        : uuidv4();
                    return {
                        ...action,
                        config: {
                            ...action.config,
                            campaignId: newCampaignId,
                        },
                    };
                }
                return action;
            });
            return { ...node, actions: newActions };
        }
        return node;
    });

    // If no old campaign IDs were found, return the updated config
    if (campaignIdMapping.size === 0) {
        return { nodes: updatedNodes };
    }

    // Serialize the entire config to JSON string for regex replacement
    let configString = JSON.stringify({ nodes: updatedNodes });

    // Replace all occurrences of old campaign IDs (UUIDs) with new ones using regex
    campaignIdMapping.forEach((newId, oldId) => {
        // UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
        // Escape special regex characters (though UUIDs typically only have hyphens which aren't special)
        const escapedOldId = oldId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Create a regex that matches the complete UUID (ensuring we match the full UUID, not a substring)
        // Use word boundaries or ensure we match the complete UUID pattern
        const uuidPattern = new RegExp(escapedOldId, 'g');
        configString = configString.replace(uuidPattern, newId);
    });

    // Parse back to JourneyNodesConfig
    const updatedConfig: JourneyNodesConfig = JSON.parse(
        configString,
    ) as JourneyNodesConfig;

    return updatedConfig;
};

/**
 * this function checks if the conversion trigger is an in and out trigger
 * @param value
 * @returns boolean where true means the conversion trigger is an in and out trigger
 */
export function isAudienceInAndOutConversionTrigger(
    value: ConversionTrigger,
): boolean {
    return (
        value.associatedAudienceIds != null &&
        value.associatedAudienceIds.length > 0
    );
}

/**
 * this function checks if the exit trigger is an audience in/out trigger
 * @param value
 * @returns boolean where true means the exit trigger is an audience in/out trigger
 */
export function isAudienceInAndOutExitTrigger(value: ExitTrigger): boolean {
    return (
        value.associatedAudienceIds != null &&
        value.associatedAudienceIds.length > 0
    );
}

/**
 * this function is used to give the current node is warehouse wait node or not
 */
export const isWarehouseWaitNode = (node: JourneyNode): boolean =>
    node.metadata?.blockId === WAIT_FOR_WH_CACHE_BLOCK_ID;

export const isAiClassifyNode = (node: JourneyNode | undefined): boolean =>
    Boolean(
        node?.actions.some((action) => action.type === ActionType.AI_CLASSIFY),
    );

/** Suffix for the hidden Split owned by an AI Classify node (like `-converge`). */
export const AI_CLASSIFY_SPLIT_NODE_ID_SUFFIX = '-ai-split';

export const getAiClassifySplitNodeId = (classifyNodeId: string): string =>
    `${classifyNodeId}${AI_CLASSIFY_SPLIT_NODE_ID_SUFFIX}`;

export const getAiClassifyNodeIdFromPairedSplitId = (
    splitNodeId: string,
): string | undefined =>
    splitNodeId.endsWith(AI_CLASSIFY_SPLIT_NODE_ID_SUFFIX)
        ? splitNodeId.slice(0, -AI_CLASSIFY_SPLIT_NODE_ID_SUFFIX.length)
        : undefined;

/**
 * Hidden Split paired with an AI Classify node — identified by
 * `${classifyNodeId}-ai-split` (not shown on canvas).
 */
export const isAiClassifyPairedSplit = (
    node: JourneyNode | undefined,
): boolean =>
    Boolean(
        node &&
        getAiClassifyNodeIdFromPairedSplitId(node.id) &&
        node.actions.some((action) => action.type === ActionType.SPLIT),
    );

/**
 * Field id used in Split path conditions for an AI Classify output, e.g.
 * SRT_JOURNEY_EVENT_AI_CLASSIFY_<nodeId>_<outputField>
 */
export const getAiClassifyFieldId = (
    classifyNodeId: string,
    outputField: string,
): string =>
    `${JourneyParamsNames.JOURNEY_EVENT}_${ActionType.AI_CLASSIFY}_${classifyNodeId}_${outputField}`;

export const getPairedSplitNode = (
    nodes: JourneyNode[],
    classifyNodeId: string,
): JourneyNode | undefined =>
    nodes.find((n) => n.id === getAiClassifySplitNodeId(classifyNodeId));

/**
 * When the canvas parent is an AI Classify node, branch destinations live on
 * the paired Split — resolve that owner node for config/UI lookups.
 */
export const resolveBranchConfigOwnerNode = (
    nodes: JourneyNode[],
    rfParentNodeId: string,
): JourneyNode | undefined => {
    const parent = nodes.find((n) => n.id === rfParentNodeId);
    if (!isAiClassifyNode(parent)) {
        return parent;
    }
    return getPairedSplitNode(nodes, rfParentNodeId) ?? parent;
};

/**
 * When the canvas parent is an AI Classify node, branch destinations live on
 * the paired Split — resolve that owner id for config mutations.
 */
export const resolveBranchConfigOwnerNodeId = (
    nodes: JourneyNode[],
    rfParentNodeId: string,
): string =>
    resolveBranchConfigOwnerNode(nodes, rfParentNodeId)?.id ?? rfParentNodeId;

export const createAiClassifySplitCondition = (
    fieldId: string,
    classification: string,
): JourneyFiltersConfig => ({
    id: uuidv4(),
    journeyFilters: {
        and: [
            {
                id: uuidv4(),
                target: { fieldId },
                operator: JourneyConditionalOperator.EQUALS,
                values: [classification],
            } as JourneyFilterRule,
        ],
    },
    audienceFilters: undefined,
});

export const getClassificationValueFromBranch = (
    branch: Branch,
): string | undefined => {
    const journeyFilters = branch.conditions?.journeyFilters;
    if (!journeyFilters || !('and' in journeyFilters)) {
        return undefined;
    }
    const rule = journeyFilters.and[0];
    if (
        rule &&
        'operator' in rule &&
        rule.operator === JourneyConditionalOperator.EQUALS &&
        Array.isArray(rule.values) &&
        rule.values.length > 0
    ) {
        return String(rule.values[0]);
    }
    return undefined;
};

export type SyncAiClassifySplitBranchesResult = {
    splitNode: JourneyNode;
    /** Destination ids that need a new CREATE_NODE in the RF graph */
    addedDestinationIds: string[];
    /** Destination ids removed from Split branches (may need RF cleanup) */
    removedDestinationIds: string[];
};

/**
 * Rebuilds paired Split branches from AI Classify classifications.
 * Preserves destinations when a classification label still exists.
 * Empty Everyone else is omitted from the payload (same as Split): the
 * frontend keeps a canvas-only CREATE_NODE + DEFAULT edge for it.
 */
export const syncAiClassifySplitBranches = ({
    splitNode,
    classifyNodeId,
    classifications,
    outputField,
    generateDestinationId,
    persistedNodeIds,
}: {
    splitNode: JourneyNode;
    classifyNodeId: string;
    classifications: string[];
    outputField: string;
    generateDestinationId: () => string;
    persistedNodeIds?: Set<string>;
}): SyncAiClassifySplitBranchesResult => {
    const fieldId = getAiClassifyFieldId(classifyNodeId, outputField);
    const existingBranches = splitNode.branchConfig?.children?.branches ?? [];
    const existingDefault = existingBranches.find((b) => b.isDefault);
    const existingQualified = existingBranches.filter((b) => !b.isDefault);

    const byClassification = new Map<string, Branch>();
    for (const branch of existingQualified) {
        const value = getClassificationValueFromBranch(branch);
        if (value) {
            byClassification.set(value, branch);
        }
    }

    const addedDestinationIds: string[] = [];
    const keptDestinationIds = new Set<string>();

    const qualifiedBranches: Branch[] = classifications.map(
        (classification) => {
            const existing = byClassification.get(classification);
            if (existing) {
                keptDestinationIds.add(existing.destination);
                return {
                    ...existing,
                    isDefault: false,
                    conditions: createAiClassifySplitCondition(
                        fieldId,
                        classification,
                    ),
                };
            }
            const destination = generateDestinationId();
            addedDestinationIds.push(destination);
            keptDestinationIds.add(destination);
            return {
                destination,
                isDefault: false,
                conditions: createAiClassifySplitCondition(
                    fieldId,
                    classification,
                ),
            };
        },
    );

    const removedDestinationIds = existingQualified
        .map((b) => b.destination)
        .filter((id) => !keptDestinationIds.has(id));

    const keepDefault =
        existingDefault !== undefined &&
        (persistedNodeIds === undefined ||
            persistedNodeIds.has(existingDefault.destination));

    const defaultBranch: Branch | undefined =
        existingDefault && keepDefault
            ? { ...existingDefault, isDefault: true, conditions: undefined }
            : undefined;

    const canonicalSplitId = getAiClassifySplitNodeId(classifyNodeId);
    const updatedSplitNode: JourneyNode = {
        ...splitNode,
        id: canonicalSplitId,
        branchConfig: {
            type: BranchConditionalTypes.IFELSE,
            children: {
                type: BranchConcurrencyTypes.PARALLEL,
                branches: defaultBranch
                    ? [...qualifiedBranches, defaultBranch]
                    : qualifiedBranches,
            },
        },
        metadata: {
            blockId: splitNode.metadata?.blockId ?? SPLIT_BLOCK_ID,
        },
    };

    return {
        splitNode: updatedSplitNode,
        addedDestinationIds,
        removedDestinationIds,
    };
};

/**
 * Strip legacy pairing metadata keys from a node (pairedSplitNodeId /
 * pairedAiClassifyNodeId). Keeps only blockId.
 */
const stripAiClassifyPairingMetadata = (node: JourneyNode): JourneyNode => {
    if (!node.metadata) {
        return node;
    }
    const meta = node.metadata as JourneyNode['metadata'] & {
        pairedSplitNodeId?: string;
        pairedAiClassifyNodeId?: string;
    };
    if (
        meta.pairedSplitNodeId === undefined &&
        meta.pairedAiClassifyNodeId === undefined
    ) {
        return node;
    }
    return {
        ...node,
        metadata: { blockId: meta.blockId },
    };
};

/**
 * Normalize legacy Classify → Split pairs so the Split id is
 * `${classifyNodeId}-ai-split` (and strip any old pairing metadata).
 */
export const migrateAiClassifyPairedSplitIds = (
    nodes: JourneyNode[],
): JourneyNode[] => {
    const classifyNodes = nodes.filter(isAiClassifyNode);
    if (classifyNodes.length === 0) {
        return nodes.map((n) => stripAiClassifyPairingMetadata(n));
    }

    const renames = new Map<string, string>(); // oldSplitId → canonicalId

    for (const classifyNode of classifyNodes) {
        const canonicalId = getAiClassifySplitNodeId(classifyNode.id);
        const childId =
            classifyNode.branchConfig?.children?.branches?.[0]?.destination;
        const child = childId ? nodes.find((n) => n.id === childId) : undefined;
        const isLegacySplitChild =
            !!childId &&
            childId !== canonicalId &&
            !!child?.actions.some((a) => a.type === ActionType.SPLIT) &&
            (classifyNode.branchConfig?.children?.branches?.length ?? 0) === 1;
        if (isLegacySplitChild && childId) {
            renames.set(childId, canonicalId);
        }
    }

    if (renames.size === 0) {
        return nodes.map((n) => stripAiClassifyPairingMetadata(n));
    }

    return nodes.map((node) => {
        let next = stripAiClassifyPairingMetadata(node);

        const newId = renames.get(next.id);
        if (newId) {
            next = { ...next, id: newId };
        }

        if (isAiClassifyNode(next)) {
            const branches = next.branchConfig?.children?.branches ?? [];
            const updatedBranches = branches.map((branch) => {
                const renamed = renames.get(branch.destination);
                return renamed ? { ...branch, destination: renamed } : branch;
            });
            if (
                updatedBranches.some(
                    (b, i) => b.destination !== branches[i]?.destination,
                )
            ) {
                next = {
                    ...next,
                    branchConfig: {
                        type: BranchConditionalTypes.IFELSE,
                        children: {
                            type:
                                next.branchConfig?.children.type ??
                                BranchConcurrencyTypes.SEQUENTIAL,
                            branches: updatedBranches,
                        },
                    },
                };
            }
        }

        return next;
    });
};

/**
 * this function use to remove internal nodes from the journey nodes config
 */
export const removeInternalNodes = (
    journeyNodesConfig: JourneyNodesConfig,
    entryNodeId: string | undefined,
): {
    journeyNodesConfig: JourneyNodesConfig;
    entryNodeId: string | undefined;
} => {
    const waitForCacheNode = journeyNodesConfig.nodes.find((node) =>
        isWarehouseWaitNode(node),
    );
    if (waitForCacheNode) {
        const updatedNodes = journeyNodesConfig.nodes.filter(
            (node) => !isWarehouseWaitNode(node),
        );
        return {
            journeyNodesConfig: { ...journeyNodesConfig, nodes: updatedNodes },
            entryNodeId:
                waitForCacheNode.branchConfig?.children?.branches?.[0]
                    ?.destination ?? '',
        };
    }
    return {
        journeyNodesConfig,
        entryNodeId,
    };
};

/**
 * Prepares a string for JSONata evaluation by replacing the custom
 * concatenation operator (§) with the JSONata concatenation operator (&).
 * This allows users to include & in their actual content without conflicts.
 * @param {string} input - The input string with § as the concatenation operator
 * @returns {string} The string with & as the concatenation operator for JSONata
 */
export const prepareForJsonata = (input: string): string => {
    if (!input) return input;
    return input.replaceAll(CallApiConcatOperator, '&');
};

const CALL_API_HANDLEBARS_PATTERN = /{{([^{}]+)}}/g;
const CALL_API_SPLIT_VARIABLES_REGEX = new RegExp(
    `(${CallApiConcatOperator}[^${CallApiConcatOperator}]+${CallApiConcatOperator})`,
);

const removeTrailingLeadingQuotesAndConcatOperator = (
    input: string,
): string => {
    const concatOperatorRegex = new RegExp(
        `^${CallApiConcatOperator}|${CallApiConcatOperator}$`,
        'g',
    );
    return input
        .replace(concatOperatorRegex, '')
        .replace(/^''|''$/g, '')
        .replace(concatOperatorRegex, '');
};

const isAlreadyJsonataStringLiteral = (input: string): boolean => {
    const trimmed = input.trim();
    return (
        (trimmed.startsWith("'") && trimmed.endsWith("'")) ||
        (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
        input.includes(CallApiConcatOperator)
    );
};

/**
 * Converts a plain string (optionally containing `{{handlebars}}` journey
 * variable references) into a valid JSONata expression: literal segments are
 * wrapped in single quotes and handlebars segments become JSONata field
 * references. Required because Call API's httpConfig.url (and headers/
 * payloadMapper) are evaluated as JSONata source at send time
 * (see coreActivities.ts) rather than treated as plain strings — an
 * unquoted literal like a bare URL is parsed as a JSONata path expression
 * and fails evaluation. Shared by the journey builder UI and the AI/MCP
 * journey tools so both produce the same valid shape.
 *
 * A no-op if the input is already a JSONata string literal or already
 * contains the concat operator — protects callers (like the AI/MCP tools)
 * that might read back an already-stringified value (e.g. from get_journey)
 * and pass it straight through, which would otherwise get double-quoted.
 * @param {string} input - The raw string, e.g. `Hello {{name}}!`
 * @returns {string} The JSONata-safe string, e.g. `'Hello ' & §$.\`name\`§ & '!'`
 */
export const stringifyStringWithVariables = (input: string): string => {
    if (isAlreadyJsonataStringLiteral(input)) return input;

    const replacedVariables = input.replaceAll(
        CALL_API_HANDLEBARS_PATTERN,
        `${CallApiConcatOperator}${JOURNEY_DYNAMIC_VARIABLE_PREFIX}\`$1\`${CallApiConcatOperator}`,
    );

    let result = replacedVariables
        .split(CALL_API_SPLIT_VARIABLES_REGEX)
        .map((part) =>
            part.startsWith(CallApiConcatOperator) ? part : `'${part}'`,
        )
        .join('');

    result = removeTrailingLeadingQuotesAndConcatOperator(result);

    return result;
};

export const getDefaultTimeConfig = (
    timeConfigType: TimeConfigType,
): DurationTimeConfig | DateTimeConfig | HoldConfig => {
    switch (timeConfigType) {
        case TimeConfigType.DURATION:
            return {
                timeConfigType: TimeConfigType.DURATION,
                duration: 0,
            };
        case TimeConfigType.HOLD:
            return {
                timeConfigType: TimeConfigType.HOLD,
                boundaries: {
                    type: HoldBoundaryType.DAY_OF_WEEK,
                    allowedDays: [],
                    timeWindows: [],
                },
            };
        case TimeConfigType.DATE_TIME:
            return {
                timeConfigType: TimeConfigType.DATE_TIME,
                dateTime: { value: '' },
                timeDifference: {
                    value: 0,
                    operator: DifferenceOperator.ADD,
                },
            };
        default:
            return assertUnreachable(
                timeConfigType,
                `Unknown timeConfigType: ${timeConfigType}`,
            );
    }
};

export function getJourneyDynamicFields(
    journey: Partial<JourneyAndVersionConfig>,
): string[] {
    const dynamicFields: string[] = [];

    journey.config?.nodes.forEach((node) => {
        node.actions.forEach((action) => {
            if (action.type === ActionType.WAIT_UNTIL) {
                if (
                    action.config.timeConfig.timeConfigType ===
                        TimeConfigType.DATE_TIME &&
                    typeof action.config.timeConfig.dateTime.value !==
                        'number' &&
                    action.config.timeConfig.dateTime.value.startsWith(
                        JOURNEY_DYNAMIC_VARIABLE_PREFIX,
                    )
                ) {
                    dynamicFields.push(
                        action.config.timeConfig.dateTime.value.split(
                            JOURNEY_DYNAMIC_VARIABLE_PREFIX,
                        )[1],
                    );
                }
            } else if (action.type === ActionType.WAIT) {
                if (
                    action.config.timeConfig.timeConfigType ===
                        TimeConfigType.DATE_TIME &&
                    typeof action.config.timeConfig.dateTime.value !==
                        'number' &&
                    action.config.timeConfig.dateTime.value.startsWith(
                        JOURNEY_DYNAMIC_VARIABLE_PREFIX,
                    )
                ) {
                    dynamicFields.push(
                        action.config.timeConfig.dateTime.value.split(
                            JOURNEY_DYNAMIC_VARIABLE_PREFIX,
                        )[1],
                    );
                }
            } else if (action.type === ActionType.SEND_MESSAGE) {
                const contentMappingsByVariant = action.config.messageConfig
                    .contentMappings as unknown as
                    | Record<
                          string,
                          Record<string, Record<string, { value?: string }>>
                      >
                    | undefined;
                const firstVariant = Object.keys(
                    contentMappingsByVariant ?? {},
                )[0];
                const variantMappings = firstVariant
                    ? contentMappingsByVariant?.[firstVariant]
                    : undefined;
                if (variantMappings) {
                    Object.keys(variantMappings).forEach((key1) => {
                        Object.keys(variantMappings[key1] ?? {}).forEach(
                            (key2) => {
                                const mappingObject =
                                    variantMappings[key1]?.[key2];
                                if (
                                    mappingObject?.value?.startsWith(
                                        JOURNEY_DYNAMIC_VARIABLE_PREFIX,
                                    )
                                ) {
                                    dynamicFields.push(
                                        mappingObject.value!.split(
                                            JOURNEY_DYNAMIC_VARIABLE_PREFIX,
                                        )[1],
                                    );
                                }
                            },
                        );
                    });
                }
                const { messageConfig } = action.config;
                if (messageConfig?.sendTo && messageConfig.channel) {
                    const sendToValue =
                        messageConfig.sendTo[messageConfig.channel];
                    if (
                        typeof sendToValue === 'string' &&
                        sendToValue.startsWith(JOURNEY_DYNAMIC_VARIABLE_PREFIX)
                    ) {
                        dynamicFields.push(
                            sendToValue.split(
                                JOURNEY_DYNAMIC_VARIABLE_PREFIX,
                            )[1],
                        );
                    }
                }
            } else if (action.type === ActionType.API) {
                dynamicFields.push(...(action.warehouseFields ?? []));
            }
        });
    });

    return dynamicFields;
}

/**
 * Encodes a single variant's ContentMappings for use in a journey Send Message
 * node by prefixing DYNAMIC variable values with `$.`.
 *
 * The journey runtime uses `$.` to resolve values from the user's journey
 * context. Only DYNAMIC type variables are prefixed; STATIC variables are
 * passed through unchanged.
 *
 * Already-prefixed values are not double-prefixed.
 */
export function encodeContentMappings(
    contentMappings: ContentMappings,
): ContentMappings {
    if (!contentMappings) {
        return {} as ContentMappings;
    }
    const result: Record<string, unknown> = {};
    for (const [section, sectionVars] of Object.entries(contentMappings)) {
        if (sectionVars == null || typeof sectionVars !== 'object') {
            result[section] = sectionVars;
        } else {
            const encodedSection: Record<string, Variable> = {};
            for (const [key, variable] of Object.entries(
                sectionVars as Record<string, Variable>,
            )) {
                encodedSection[key] = {
                    ...variable,
                    value:
                        variable.type !== CampaignFieldType.STATIC &&
                        variable.value != null &&
                        !variable.value.startsWith(
                            JOURNEY_DYNAMIC_VARIABLE_PREFIX,
                        )
                            ? `${JOURNEY_DYNAMIC_VARIABLE_PREFIX}${variable.value}`
                            : variable.value,
                };
            }
            result[section] = encodedSection;
        }
    }
    return result as ContentMappings;
}

/**
 * Decodes a single variant's ContentMappings by stripping the `$.` prefix
 * from variable values where present. Inverse of `encodeContentMappings`.
 */
export function decodeContentMappings(
    contentMappings: ContentMappings,
): ContentMappings {
    if (!contentMappings) return {};
    const result: Record<string, unknown> = {};
    for (const [section, sectionVars] of Object.entries(contentMappings)) {
        if (sectionVars == null || typeof sectionVars !== 'object') {
            result[section] = sectionVars;
        } else {
            const decodedSection: Record<string, Variable> = {};
            for (const [key, variable] of Object.entries(
                sectionVars as Record<string, Variable>,
            )) {
                decodedSection[key] = {
                    ...variable,
                    value:
                        variable.value != null &&
                        variable.value.startsWith(
                            JOURNEY_DYNAMIC_VARIABLE_PREFIX,
                        )
                            ? variable.value.slice(
                                  JOURNEY_DYNAMIC_VARIABLE_PREFIX.length,
                              )
                            : variable.value,
                };
            }
            result[section] = decodedSection;
        }
    }
    return result as ContentMappings;
}

/**
 * Encodes a campaign's `sendTo` for use in a journey Send Message node.
 * Prefixes the channel value with `$.` so the journey runtime resolves it
 * from the user's warehouse context. Mirrors the inverse FE decode logic.
 * Already-prefixed values are not double-prefixed.
 */
export function encodeSendToForJourney(
    sendTo: SendTo,
    channel: CommunicationChannel,
): SendTo {
    const channelValue = sendTo[channel];
    if (!channelValue) return sendTo;
    const encodedValue = channelValue.startsWith(
        JOURNEY_DYNAMIC_VARIABLE_PREFIX,
    )
        ? channelValue
        : `${JOURNEY_DYNAMIC_VARIABLE_PREFIX}${channelValue}`;
    return {
        ...sendTo,
        [channel]: encodedValue,
        type: sendTo.type ?? FieldType.DIMENSION,
    };
}

/** True when any node in the journey config has an EXPERIMENT action. */
export const configHasExperimentNode = (
    config: JourneyNodesConfig | undefined,
): boolean =>
    !!config?.nodes?.some((node) =>
        node.actions?.some((action) => action.type === ActionType.EXPERIMENT),
    );

type JourneyNodeId = JourneyNode['id'];

export const getDefaultJourneyNodeTitle = (
    node: JourneyNode,
): string | null => {
    const blockId = node.metadata?.blockId;
    if (!blockId) return null;

    const sendMessageAction = node.actions.find(
        (action) => action.type === ActionType.SEND_MESSAGE,
    );

    if (sendMessageAction) {
        const channelType =
            sendMessageAction.actionConfig?.channelType ??
            sendMessageAction.config.messageConfig.channel;

        switch (channelType) {
            case CommunicationChannel.EMAIL:
                return 'Untitled email';
            case CommunicationChannel.SMS:
                return 'Untitled SMS';
            case CommunicationChannel.WHATSAPP:
                return 'Untitled WhatsApp message';
            default:
                return 'Untitled';
        }
    }

    return journeyBlocks.find((block) => block.id === blockId)?.title ?? null;
};

/** Catalog / builder Untitled* titles that still count as default aliases. */
const getJourneyNodeDefaultTitleAliases = (node: JourneyNode): string[] => {
    const aliases = new Set<string>();
    const untitled = getDefaultJourneyNodeTitle(node);
    if (untitled) aliases.add(untitled);

    const blockId = node.metadata?.blockId;
    if (blockId) {
        const catalogTitle = journeyBlocks.find(
            (block) => block.id === blockId,
        )?.title;
        if (catalogTitle) aliases.add(catalogTitle);
    }

    // Numbered duplicates from the builder ("Untitled email (2)").
    for (const base of [...aliases]) {
        aliases.add(`${base} (2)`);
        aliases.add(`${base} (3)`);
    }

    return [...aliases];
};

/**
 * True when the node title is still in the default-alias set for its kind
 * (Untitled*, catalog title, numbered variants).
 */
export const isJourneyNodeTitleDefault = (node: JourneyNode): boolean => {
    const title = node.title.trim();
    if (!title) return true;
    const aliases = getJourneyNodeDefaultTitleAliases(node);
    if (aliases.some((alias) => alias === title)) return true;

    // Any "Untitled … (N)" variant for the builder Untitled base.
    const untitled = getDefaultJourneyNodeTitle(node);
    if (untitled) {
        const numbered = new RegExp(
            `^${untitled.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?: \\(\\d+\\))?$`,
        );
        if (numbered.test(title)) return true;
    }
    return false;
};

export const isJourneyDefaultTitleAlias = (title: string): boolean => {
    const trimmed = title.trim();
    if (!trimmed) return true;
    if (trimmed === 'Untitled Journey') return true;
    if (/^Untitled Journey(?: \(\d+\))?$/u.test(trimmed)) return true;
    return false;
};

export const getDefaultJourneyNodeDescription = (node: JourneyNode): string => {
    const blockId = node.metadata?.blockId;
    if (!blockId) return '';
    return (
        journeyBlocks.find((block) => block.id === blockId)?.description ?? ''
    );
};

export const isJourneyNodeDescriptionDefault = (node: JourneyNode): boolean =>
    node.description.trim() === getDefaultJourneyNodeDescription(node).trim();

/**
 * Ids that are regenerated on every seed and therefore say nothing about
 * whether the user configured the node.
 */
const VOLATILE_SEED_KEYS = new Set([
    'id',
    'campaignId',
    'experimentId',
    'variantId',
    'nodeId',
]);

const stripSeedVolatileKeys = (value: unknown): unknown => {
    if (Array.isArray(value)) {
        return value.map(stripSeedVolatileKeys);
    }
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>)
                .filter(
                    ([key, entry]) =>
                        !VOLATILE_SEED_KEYS.has(key) && entry !== undefined,
                )
                .map(([key, entry]): [string, unknown] => [
                    key,
                    stripSeedVolatileKeys(entry),
                ])
                .sort(([a], [b]) => a.localeCompare(b)),
        );
    }
    return value;
};

/** Stable JSON shape for fingerprinting — keeps entity ids the user picks. */
const stableNormalizeConfig = (value: unknown): unknown => {
    if (Array.isArray(value)) {
        return value.map(stableNormalizeConfig);
    }
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>)
                .filter(([, entry]) => entry !== undefined)
                .map(([key, entry]): [string, unknown] => [
                    key,
                    stableNormalizeConfig(entry),
                ])
                .sort(([a], [b]) => a.localeCompare(b)),
        );
    }
    return value;
};

const isStructurallyEqualIgnoringVolatileKeys = (
    a: unknown,
    b: unknown,
): boolean =>
    JSON.stringify(stripSeedVolatileKeys(a)) ===
    JSON.stringify(stripSeedVolatileKeys(b));

/**
 * Stable key for "what the user configured on this node", deliberately ignoring
 * title, description and metadata — those are what the AI namer writes back, so
 * including them would make its own answer look like a user edit.
 *
 * Unlike seed-default comparison, entity ids (e.g. templateDetails.v1.id) are
 * kept so template-only edits change the fingerprint.
 */
export const getJourneyNodeConfigFingerprint = (node: JourneyNode): string =>
    JSON.stringify(
        stableNormalizeConfig({
            actions: node.actions,
            branchConfig: node.branchConfig,
            exitConfig: node.exitConfig,
        }),
    );

const getSeededActionsForNode = (node: JourneyNode): JourneyAction[] => {
    const blockId = node.metadata?.blockId;
    if (!blockId) return [];

    const block = journeyBlocks.find((candidate) => candidate.id === blockId);
    if (!block) return [];

    return getActionSeedData(block.actions);
};

const COMPARED_ACTION_KEYS = [
    'config',
    'warehouseFields',
    'actionConfig',
    'uiConfig',
] as const;

const isSeededAction = (
    action: JourneyAction,
    seeded: JourneyAction,
): boolean => {
    if (action.type !== seeded.type) return false;

    return COMPARED_ACTION_KEYS.every((key) => {
        // A key the node omits carries no user intent — nodes built outside the
        // builder (journey templates, MCP tools) legitimately skip uiConfig and
        // actionConfig.
        if (!(key in action)) return true;

        return isStructurallyEqualIgnoringVolatileKeys(
            (action as Record<string, unknown>)[key],
            (seeded as Record<string, unknown>)[key],
        );
    });
};

/**
 * True when the node still holds exactly what dropping its block produced, so
 * there is nothing worth naming yet.
 *
 * Compared against live seed data rather than a hand-written list of defaults,
 * so changing a seed can't silently break naming.
 */
export const isJourneyNodeActionPayloadDefault = (
    node: JourneyNode,
): boolean => {
    const seededActions = getSeededActionsForNode(node);
    if (seededActions.length !== node.actions.length) return false;

    const actionsAreSeeded = node.actions.every((action, index) => {
        const seeded = seededActions[index];
        return seeded !== undefined && isSeededAction(action, seeded);
    });
    if (!actionsAreSeeded) return false;

    // Split/experiment blocks carry their user input on branchConfig, not actions.
    const branchChildren = node.branchConfig?.children;
    const branches =
        branchChildren && 'branches' in branchChildren
            ? branchChildren.branches
            : [];

    return branches.every(
        (branch) =>
            !branch.conditions ||
            (branch.conditions.journeyFilters === undefined &&
                branch.conditions.audienceFilters === undefined),
    );
};

export type AiEligibleJourneyNodeOptions = {
    /** Restrict to these nodes. Omit to consider every node in the journey. */
    nodeIds?: JourneyNodeId[];
};

/**
 * Nodes worth sending to the AI namer: still on default title/description
 * aliases, and hasMeaningfulNamingSignal (not seed-diff). One-shot only —
 * once a non-default name is on the node (AI or user), later config edits do
 * not rename it.
 */
export const getAiEligibleJourneyNodeIds = (
    journey: { config?: JourneyNodesConfig | undefined },
    options: AiEligibleJourneyNodeOptions = {},
): JourneyNodeId[] => {
    const { nodeIds } = options;
    const nodes = journey.config?.nodes ?? [];
    const candidates = nodeIds
        ? nodes.filter((node) => nodeIds.includes(node.id))
        : nodes;

    return candidates
        .filter(
            (node) =>
                isJourneyNodeTitleDefault(node) &&
                isJourneyNodeDescriptionDefault(node) &&
                hasMeaningfulNamingSignal(node),
        )
        .map((node) => node.id);
};
